import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import './BroadcastCard.css';

const BroadcastCard = ({ userEmail, token, standalone = false }) => {
    const [currentCard, setCurrentCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [hasEngaged, setHasEngaged] = useState(false);
    const [showAccepted, setShowAccepted] = useState(false); // show accepted overlay after accept
    const [showDetails, setShowDetails] = useState(false); // dashboard: open full view

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

    const [debugInfo, setDebugInfo] = useState(null);
    const [audioReady, setAudioReady] = useState(false);
    const audioCtxRef = useRef(null);
    const audioUnlockedRef = useRef(false);
    const lastActionRef = useRef({ type: null, t: 0 });
    const suppressShowRef = useRef(false); // prevent auto-show during dismiss cycles
    const appearPlayedForIdRef = useRef(null); // legacy: per-card guard (kept but not required)
    const lastAppearPlayAtRef = useRef(0); // time-based throttle for appear sound
    const appearPendingForIdRef = useRef(null); // pending appear for when audio unlocks
    const appearRetryTimerRef = useRef(null); // retry timer to play appear after unlock
    const appearFallbackTimerRef = useRef(null); // short fallback after paint if unlocked
    const autoDismissTimerRef = useRef(null); // stable timer for 15s auto-dismiss
    const visibleAtRef = useRef(0); // timestamp when overlay became visible
    // Short time-based guard to prevent duplicate saves from the same show cycle
    const timeoutSavedRef = useRef({ id: null, t: 0 });

    const containerRef = useRef(null);
    const wrapperRef = useRef(null); // animated wrapper with transition classes
    const appearRafRef = useRef(0); // RAF id to align sound with first painted frame

    const ensureAudio = () => {
        if (!audioCtxRef.current) {
            try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { }
        }
        return audioCtxRef.current;
    };

    const tryResumeAudio = async () => {
        const ctx = ensureAudio();
        if (!ctx) return false;
        try {
            if (ctx.state === 'suspended' && ctx.resume) {
                await ctx.resume();
            }
            audioUnlockedRef.current = true;
            setAudioReady(ctx.state === 'running');
            return ctx.state === 'running';
        } catch {
            return false;
        }
    };

    const maybePlayAppearSound = () => {
        if (!isVisible) return false;
        const now = Date.now();
        // Throttle to avoid accidental double plays from multiple triggers
        if (lastAppearPlayAtRef.current && (now - lastAppearPlayAtRef.current) < 800) return false;
        if (audioUnlockedRef.current || (audioCtxRef.current && audioCtxRef.current.state === 'running')) {
            playAppearSound();
            lastAppearPlayAtRef.current = now;
            setAudioReady(true);
            // keep ID updated for other legacy paths
            if (currentCard) appearPlayedForIdRef.current = currentCard._id;
            return true;
        }
        return false;
    };

    const forcePlayAppearIfReady = () => {
        if (!isVisible) return false;
        const now = Date.now();
        if (lastAppearPlayAtRef.current && (now - lastAppearPlayAtRef.current) < 800) return false;
        if (audioUnlockedRef.current || (audioCtxRef.current && audioCtxRef.current.state === 'running')) {
            playAppearSound();
            lastAppearPlayAtRef.current = now;
            setAudioReady(true);
            if (currentCard) appearPlayedForIdRef.current = currentCard._id;
            return true;
        }
        return false;
    };

    useEffect(() => {
        // Unlock WebAudio after early user interactions so appear sounds can play before button clicks
        const unlock = async () => {
            const ctx = ensureAudio();
            if (!ctx || audioUnlockedRef.current) return;
            try {
                if (ctx.state === 'suspended') await ctx.resume();
                audioUnlockedRef.current = true;
                setAudioReady(ctx.state === 'running');
            } catch { }
        };
        const onPointerDown = () => unlock();
        const onPointerMove = () => unlock();
        const onPointerUp = () => unlock();
        const onMouseMove = () => unlock();
        const onTouchStart = () => unlock();
        const onKey = () => unlock();
        window.addEventListener('pointerdown', onPointerDown, { once: true });
        window.addEventListener('pointermove', onPointerMove, { once: true });
        window.addEventListener('pointerup', onPointerUp, { once: true });
        window.addEventListener('mousemove', onMouseMove, { once: true });
        window.addEventListener('touchstart', onTouchStart, { once: true });
        window.addEventListener('keydown', onKey, { once: true });
        return () => {
            window.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    const playTone = (freq = 880, durationMs = 140, type = 'sine', gain = 0.12) => {
        const ctx = ensureAudio();
        if (!ctx) return;
        // Try to resume immediately before playing
        try { if (ctx.state === 'suspended' && ctx.resume) ctx.resume(); } catch { }
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = type;
        o.frequency.value = freq;
        g.gain.value = 0;
        o.connect(g);
        g.connect(ctx.destination);
        const now = ctx.currentTime;
        // Simple attack/decay envelope
        g.gain.linearRampToValueAtTime(gain, now + 0.01);
        g.gain.linearRampToValueAtTime(0.0001, now + durationMs / 1000);
        o.start(now);
        o.stop(now + durationMs / 1000 + 0.02);
    };

    const playAcceptSound = () => {
        // Upward two-note chime
        playTone(880, 120, 'triangle', 0.12);
        setTimeout(() => playTone(1175, 140, 'triangle', 0.12), 110);
    };

    const playLaterSound = () => {
        // Soft click/bloop
        playTone(320, 90, 'sine', 0.10);
        setTimeout(() => playTone(220, 80, 'sine', 0.08), 70);
    };

    const playAppearSound = () => {
        // Gentle shimmer when the card appears
        playTone(660, 90, 'sine', 0.08);
        setTimeout(() => playTone(990, 100, 'triangle', 0.08), 80);
        setTimeout(() => playTone(1320, 110, 'triangle', 0.06), 150);
    };
    const isAdminOverlay = (() => {
        try { return typeof window !== 'undefined' && window.location && window.location.pathname.includes('/admin/overlay'); } catch { return false; }
    })();

    const showDebug = (() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            return standalone && sp.get('debug') === '1';
        } catch {
            return false;
        }
    })();

    useEffect(() => {
        const poll = async () => {
            try {
                setLoading(true);
                // 1) Always try public latest first
                const pub = await axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/latest-public`, { headers: { 'Cache-Control': 'no-store' } });
                setDebugInfo({ source: userEmail && token ? 'latest-public(first,auth-present)' : 'latest-public(first,unauth)', raw: pub.data });
                const publicCard = pub.data?.success ? pub.data.card : null;
                if (publicCard && publicCard.isPublished === true) {
                    // Suppress public card if already seen today (both modes)
                    if (hasSeenCard(publicCard._id)) {
                        // do not return; allow auth-specific fetch below
                    } else {
                        if (!currentCard || currentCard._id !== publicCard._id) {
                            setIsVisible(false);
                            setCurrentCard(publicCard);
                            setHasEngaged(false);
                            setTimeout(() => { setIsVisible(true); }, 10);
                        }
                        return; // already have a card, stop here
                    }
                }

                // 2) If logged in, try current authenticated
                if (userEmail && token) {
                    try {
                        const response = await axios.get(
                            `${API_BASE_URL}/auth/user/broadcast-cards/current`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'user-info': userEmail
                                }
                            }
                        );
                        setDebugInfo({ source: 'current(auth-fallback)', raw: response.data });
                        if (response.data?.success && response.data.card) {
                            const newCard = response.data.card;
                            // Suppress if already seen today
                            if (hasSeenCard(newCard._id)) {
                                setIsVisible(false);
                                return;
                            }
                            if (!currentCard || currentCard._id !== newCard._id) {
                                setIsVisible(false);
                                setCurrentCard(newCard);
                                setHasEngaged(false);
                                setTimeout(() => { setIsVisible(true); }, 10);
                            }
                            return;
                        }
                    } catch (e) {
                        setDebugInfo(prev => ({ ...(prev || {}), errAuth: String(e) }));
                    }
                }

                // 3) Nothing available
                if (currentCard) {
                    setIsVisible(false);
                    setTimeout(() => setCurrentCard(null), 300);
                }
            } catch (err) {
                console.error('BroadcastCard poll error:', err?.message || err);
                setDebugInfo(prev => ({ ...(prev || {}), pollError: err?.message || 'Network error' }));
                // Do not rethrow; prevents uncaught runtime errors on network failures
            } finally {
                setLoading(false);
            }
        };

        // initial and interval poll
        poll();
        const interval = setInterval(poll, 10000);
        return () => clearInterval(interval);
    }, [userEmail, token]);

    // Show scheduling + appear sound trigger synced to transition start, with fallback
    useEffect(() => {
        let showTimer = null;
        let cleanups = [];

        // Prepare listeners before showing so we don't miss the first transition frame
        if (currentCard) {
            const node = wrapperRef.current;
            const onStart = (e) => {
                // Ignore transition events that originate from children (like button hover)
                if (e && e.target && node && e.target !== node) return;
                const isEntranceProp = !e || !e.propertyName || e.propertyName === 'opacity' || e.propertyName === 'transform';
                if (!isEntranceProp) return;
                // Try to resume audio context right at transition start, then play immediately
                const maybe = tryResumeAudio();
                if (maybe && typeof maybe.then === 'function') {
                    maybe.finally(() => {
                        // Defer to next frame so isVisible state has committed
                        requestAnimationFrame(() => {
                            maybePlayAppearSound();
                        });
                    });
                } else {
                    // Defer to next frame so isVisible state has committed
                    requestAnimationFrame(() => {
                        maybePlayAppearSound();
                    });
                }
                // Timer will be set in the main useEffect below - removed redundant timer
            };
            if (node) {
                node.addEventListener('transitionrun', onStart);
                node.addEventListener('transitionstart', onStart);
                cleanups.push(() => {
                    node.removeEventListener('transitionrun', onStart);
                    node.removeEventListener('transitionstart', onStart);
                });
            }
        }

        if (currentCard && !isVisible) {
            if (suppressShowRef.current) {
                suppressShowRef.current = false;
            } else {
                showTimer = setTimeout(() => {
                    setIsVisible(true);
                    visibleAtRef.current = Date.now();
                    // Mark card as seen IMMEDIATELY when it becomes visible
                    // This prevents re-showing on refresh
                    if (currentCard?._id) {
                        markCardSeen(currentCard._id);
                    }
                    // First-paint backup: try to play sound immediately after first paint.
                    // Throttle inside maybePlayAppearSound() prevents double if transition events also fire.
                    requestAnimationFrame(() => {
                        const node = wrapperRef.current;
                        if (!node) return;
                        const maybe = tryResumeAudio();
                        if (maybe && typeof maybe.then === 'function') {
                            maybe.finally(() => { maybePlayAppearSound(); });
                        } else {
                            maybePlayAppearSound();
                        }
                    });
                }, 10);
            }
        }

        return () => {
            if (showTimer) clearTimeout(showTimer);
            cleanups.forEach(fn => fn());
        };
    }, [currentCard, isVisible]);

    // SINGLE 15-second auto-dismiss timer - EXACTLY 15 seconds
    useEffect(() => {
        // Clear any existing timer first
        if (autoDismissTimerRef.current) {
            clearTimeout(autoDismissTimerRef.current);
            autoDismissTimerRef.current = null;
        }

        // Start timer ONLY when card is visible and not in details view
        if (currentCard && isVisible && !showDetails) {
            console.log(`⏱️ Starting 15-second timer for card ${currentCard._id}`);
            autoDismissTimerRef.current = setTimeout(() => {
                console.log(`⏰ 15 seconds elapsed - auto-dismissing card ${currentCard._id}`);
                handleDismiss(true); // Mark as timeout
            }, 15000); // Exactly 15 seconds
        }

        // Cleanup on unmount or dependency change
        return () => {
            if (autoDismissTimerRef.current) {
                clearTimeout(autoDismissTimerRef.current);
                autoDismissTimerRef.current = null;
            }
        };
    }, [currentCard, isVisible, showDetails]);

    // Watchdog removed - single timer above is sufficient and precise

    // Reset timeout-save guard when the card becomes visible (new show cycle)
    useEffect(() => {
        if (isVisible) {
            timeoutSavedRef.current = { id: null, t: 0 };
        }
    }, [isVisible]);

    const hasSeenCard = (cardId) => {
        try {
            const key = 'seenBroadcastCards';
            const raw = localStorage.getItem(key);
            if (!raw) return false;
            const data = JSON.parse(raw);
            const today = new Date().toISOString().slice(0, 10);
            if (data.date !== today) return false;
            return Array.isArray(data.ids) && data.ids.includes(cardId);
        } catch { return false; }
    };

    const markCardSeen = (cardId) => {
        try {
            const key = 'seenBroadcastCards';
            const today = new Date().toISOString().slice(0, 10);
            const raw = localStorage.getItem(key);
            let data = { date: today, ids: [] };
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed.date === today && Array.isArray(parsed.ids)) {
                    data = parsed;
                }
            }
            if (!data.ids.includes(cardId)) data.ids.push(cardId);
            localStorage.setItem(key, JSON.stringify(data));
        } catch { }
    };

    const fetchCurrentCard = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_BASE_URL}/auth/user/broadcast-cards/current`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'user-info': userEmail
                    }
                }
            );

            if (response.data.success) {
                let newCard = response.data.card;
                // Fallback to public latest published card if none - ONLY if not authenticated
                if (!newCard && (!userEmail || !token)) {
                    try {
                        const pub = await axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/latest-public`, { headers: { 'Cache-Control': 'no-store' } });
                        if (pub.data?.success) {
                            newCard = pub.data.card;
                        }
                    } catch { }
                }

                // STRICT CLIENT-SIDE TIME CHECK
                // If the card was published more than 15 seconds ago, DO NOT SHOW IT.
                // This overrides any server-side leniency and ensures offline users don't see old popups.
                if (newCard && newCard.isPublished === true && newCard.publishedAt) {
                    const publishedTime = new Date(newCard.publishedAt).getTime();
                    const now = Date.now();
                    const diffSeconds = (now - publishedTime) / 1000;

                    if (diffSeconds > 15) {
                        console.log(`[BroadcastCard] Card ${newCard._id} is too old (${diffSeconds.toFixed(1)}s). Suppressing.`);
                        if (currentCard) {
                            setIsVisible(false);
                            setTimeout(() => setCurrentCard(null), 300);
                        }
                        setLoading(false);
                        return;
                    }
                }

                // Only show published cards on any surface (dashboard or overlay)
                if (!newCard || newCard.isPublished !== true) {
                    if (currentCard) {
                        setIsVisible(false);
                        setTimeout(() => setCurrentCard(null), 300);
                    }
                    setLoading(false);
                    return;
                }

                // Only show animation if it's a different card
                if (newCard && (!currentCard || currentCard._id !== newCard._id)) {
                    // Suppress pop-up if already seen today (both modes)
                    if (hasSeenCard(newCard._id)) {
                        // keep hidden
                        setIsVisible(false);
                        setCurrentCard(null);
                        return;
                    }
                    // Smoothly swap to the new card without clearing to null (prevents re-show loop)
                    setIsVisible(false);
                    setTimeout(() => {
                        setCurrentCard(newCard);
                        setHasEngaged(false);
                        suppressShowRef.current = false;
                        visibleAtRef.current = Date.now();
                        setIsVisible(true);
                    }, 10);
                }
            }
        } catch (error) {
            // On error (including auth), fallback to public latest
            try {
                const pub = await axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/latest-public`, { headers: { 'Cache-Control': 'no-store' } });
                if (pub.data?.success && pub.data.card && pub.data.card.isPublished === true) {
                    const pCard = pub.data.card;

                    // STRICT CLIENT-SIDE TIME CHECK FOR FALLBACK
                    const publishedTime = new Date(pCard.publishedAt).getTime();
                    const now = Date.now();
                    const diffSeconds = (now - publishedTime) / 1000;

                    if (diffSeconds > 15) {
                        console.log(`[BroadcastCard] Public Card ${pCard._id} is too old (${diffSeconds.toFixed(1)}s). Suppressing.`);
                        return;
                    }

                    if (hasSeenCard(pCard._id)) {
                        return;
                    }
                    setIsVisible(false);
                    setCurrentCard(pCard);
                    setHasEngaged(false);
                    return;
                }
            } catch (e) {
                console.error('Error fetching latest public card:', e);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEngagement = async () => {
        if (!currentCard || hasEngaged) return;

        try {
            // Mark action time immediately to block any pending appear
            lastActionRef.current = { type: 'accept', t: Date.now() };
            // Cancel any pending appear sound so it doesn't fire on click
            if (appearRetryTimerRef.current) {
                clearTimeout(appearRetryTimerRef.current);
                appearRetryTimerRef.current = null;
            }
            appearPendingForIdRef.current = null;
            appearPlayedForIdRef.current = currentCard._id;
            if (isAdminOverlay) {
                // Admin overlay: act locally without backend side-effects
                setHasEngaged(true);
                if (currentCard?._id) markCardSeen(currentCard._id);

                // Trigger storage event for real-time updates
                localStorage.setItem('lastBroadcastAction', JSON.stringify({
                    action: 'accept',
                    cardId: currentCard._id,
                    timestamp: new Date().toISOString()
                }));
                window.dispatchEvent(new StorageEvent('storage', {
                    key: 'lastBroadcastAction',
                    newValue: localStorage.getItem('lastBroadcastAction')
                }));

                playAcceptSound();
                if (standalone) {
                    setShowAccepted(true);
                    setTimeout(() => { setShowAccepted(false); handleDismiss(); }, 1400);
                } else {
                    setTimeout(() => { handleDismiss(); }, 800);
                }
                return;
            }

            const response = await axios.post(
                `${API_BASE_URL}/auth/user/broadcast-cards/${currentCard._id}/engage`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'user-info': userEmail
                    }
                }
            );

            if (response.data.success) {
                setHasEngaged(true);
                // Mark as seen on Accept in all modes
                if (currentCard?._id) {
                    markCardSeen(currentCard._id);

                    // Trigger storage event for real-time updates
                    localStorage.setItem('lastBroadcastAction', JSON.stringify({
                        action: 'accept',
                        cardId: currentCard._id,
                        timestamp: new Date().toISOString()
                    }));
                    window.dispatchEvent(new StorageEvent('storage', {
                        key: 'lastBroadcastAction',
                        newValue: localStorage.getItem('lastBroadcastAction')
                    }));
                }
                // Sound: accept
                playAcceptSound();
                if (standalone) {
                    // Overlay: show accepted overlay (no flip) before dismissing
                    setShowAccepted(true);
                    setTimeout(() => {
                        setShowAccepted(false);
                        handleDismiss();
                    }, 1400);
                } else {
                    toast.success('Challenge accepted! 🎉', {
                        position: "top-right",
                        autoClose: 1000,
                    });
                    // Dismiss shortly after
                    setTimeout(() => {
                        handleDismiss();
                    }, 800);
                }
            }
        } catch (error) {
            console.error('Error engaging with card:', error);
            toast.error('Failed to accept challenge');
        }
    };

    const saveTimeoutDirect = (card) => {
        try {
            // Guard duplicates within a short window (per show cycle)
            const now = Date.now();
            if (timeoutSavedRef.current.id === card._id && (now - timeoutSavedRef.current.t) < 2000) return;
            const timeoutCards = JSON.parse(localStorage.getItem('timeoutCards') || '[]');
            const fewSecondsAgo = Date.now() - 3 * 1000;
            const exists = timeoutCards.some(tc => tc.cardId?._id === card._id && new Date(tc.createdAt).getTime() > fewSecondsAgo);
            if (!exists) {
                const entry = {
                    _id: `timeout_${card._id}_${Date.now()}`,
                    cardId: card,
                    status: 'timeout',
                    createdAt: new Date().toISOString(),
                    title: card.title || 'Timeout Card',
                    description: card.description || ''
                };
                timeoutCards.push(entry);
                localStorage.setItem('timeoutCards', JSON.stringify(timeoutCards));
                timeoutSavedRef.current = { id: card._id, t: now };
                // Manually notify listeners in same tab
                window.dispatchEvent(new StorageEvent('storage', {
                    key: 'timeoutCards',
                    newValue: JSON.stringify(timeoutCards)
                }));
            }
        } catch (e) {
            console.warn('Failed to persist timeout card locally', e);
        }
    };

    const handleDismiss = (isTimeout = false) => {
        console.log('[BroadcastCard] handleDismiss called:', { isTimeout, cardId: currentCard?._id });

        // Always mark as seen when dismissed
        if (currentCard?._id) {
            // Card is already marked as seen above
            // Backend handles offline users - no need to save to localStorage
            // This prevents duplicate cards in Challenges page
            console.log('[BroadcastCard] Card dismissed. Backend handles offline user engagement.');

            // Trigger storage event for real-time updates
            const actionData = {
                action: isTimeout ? 'timeout' : 'dismiss',
                cardId: currentCard._id,
                timestamp: new Date().toISOString()
            };
            console.log('[BroadcastCard] Triggering storage event:', actionData);
            localStorage.setItem('lastBroadcastAction', JSON.stringify(actionData));
            window.dispatchEvent(new StorageEvent('storage', {
                key: 'lastBroadcastAction',
                newValue: localStorage.getItem('lastBroadcastAction')
            }));
        }
        // Mark a last action to mute appear sound briefly
        lastActionRef.current = { type: 'dismiss', t: Date.now() };
        // Suppress any immediate auto-show while we transition out
        suppressShowRef.current = true;
        // Reset appear sound tracker for next card/show cycle
        appearPlayedForIdRef.current = null;
        // Clear timers/pendings immediately
        if (autoDismissTimerRef.current) {
            clearTimeout(autoDismissTimerRef.current);
            autoDismissTimerRef.current = null;
        }
        if (appearRetryTimerRef.current) {
            clearTimeout(appearRetryTimerRef.current);
            appearRetryTimerRef.current = null;
        }
        appearPendingForIdRef.current = null;
        setIsVisible(false);
        setTimeout(() => setCurrentCard(null), 300);
    };

    const handleLater = async () => {
        if (!currentCard) return;
        try {
            // Cancel any pending appear sound so it doesn't fire on click
            if (appearRetryTimerRef.current) {
                clearTimeout(appearRetryTimerRef.current);
                appearRetryTimerRef.current = null;
            }
            appearPendingForIdRef.current = null;
            appearPlayedForIdRef.current = currentCard._id;
            if (isAdminOverlay) {
                // Admin overlay: local snooze/dismiss behavior only
                playLaterSound();
                if (currentCard?._id) markCardSeen(currentCard._id);

                // Trigger storage event for real-time updates
                localStorage.setItem('lastBroadcastAction', JSON.stringify({
                    action: 'later',
                    cardId: currentCard._id,
                    timestamp: new Date().toISOString()
                }));
                window.dispatchEvent(new StorageEvent('storage', {
                    key: 'lastBroadcastAction',
                    newValue: localStorage.getItem('lastBroadcastAction')
                }));

                lastActionRef.current = { type: 'later', t: Date.now() };
                handleDismiss();
                return;
            }

            const response = await axios.post(
                `${API_BASE_URL}/auth/user/broadcast-cards/${currentCard._id}/later`,
                { snoozeMinutes: 10 },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'user-info': userEmail,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.data.success) {
                toast.info('Challenge snoozed for later', { autoClose: 1200 });
                // Mark as seen on Later as well so overlay doesn't re-show
                if (currentCard?._id) {
                    markCardSeen(currentCard._id);

                    // Trigger storage event for real-time updates
                    localStorage.setItem('lastBroadcastAction', JSON.stringify({
                        action: 'later',
                        cardId: currentCard._id,
                        timestamp: new Date().toISOString()
                    }));
                    window.dispatchEvent(new StorageEvent('storage', {
                        key: 'lastBroadcastAction',
                        newValue: localStorage.getItem('lastBroadcastAction')
                    }));
                }
                // note time to mute immediate appear sound
                lastActionRef.current = { type: 'later', t: Date.now() };
                // Sound: later
                playLaterSound();
                handleDismiss();
            }
        } catch (err) {
            console.error('Error marking later:', err);
            handleDismiss();
        }
    };

    if (!currentCard) {
        // In standalone (OBS) render nothing for a clean transparent scene. Debug only when ?debug=1
        if (showDebug) {
            return (
                <pre style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    maxWidth: 360,
                    maxHeight: 180,
                    overflow: 'auto',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#9ee6ff',
                    fontSize: 10,
                    padding: 8,
                    borderRadius: 6
                }}>
                    {JSON.stringify(debugInfo, null, 2)}
                </pre>
            );
        }
        return null;
    }

    return (
        <div className={`broadcast-card-container ${standalone ? 'standalone' : ''}`} ref={containerRef}>
            <div ref={wrapperRef} className={`${standalone ? 'relative z-10 w-full h-full flex items-center justify-center' : 'fixed top-4 right-4 z-50'} transition-all duration-500 transform ${isVisible ? 'translate-x-0 opacity-100' : standalone ? 'opacity-0' : 'translate-x-full opacity-0'
                }`}>
                <div className={`${standalone ? 'rounded-2xl p-3 sm:p-4 w-full max-w-[90vw] sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[60vw] xl:max-w-[50vw]' : 'rounded-xl p-2 sm:p-3 max-w-[85vw] sm:max-w-xs w-full'} shadow-2xl text-white relative overflow-visible ${hasEngaged ? 'scale-100 animate-pulse' : ''} 
                    broadcast-card-surface broadcast-compact backdrop-blur-xl`}
                    style={{
                        // Solid dark background in standalone to contrast with green screen
                        background: standalone ? 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))' : undefined
                    }}>
                    {/* Sound is auto-handled; no manual enable button by design */}
                    {/* Futuristic glow accents */}
                    {!standalone && (
                        <>
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl pointer-events-none" />
                            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-500/30 rounded-full blur-3xl pointer-events-none" />
                        </>
                    )}

                    {/* Header removed as requested */}
                    <div className="relative z-10 mb-3">
                        {/* Only keeping essential layout structure if needed, or removing entirely */}
                    </div>
                    {/* Image */}
                    {currentCard.imageUrl && (
                        <div className="relative z-10 mb-3">
                            <img
                                src={currentCard.imageUrl?.startsWith('http') ? currentCard.imageUrl : `${API_BASE_URL}${currentCard.imageUrl}`}
                                alt={currentCard.title}
                                className={`w-full ${standalone ? 'max-h-[60vh] sm:max-h-[70vh]' : 'max-h-32 sm:max-h-40 md:max-h-44'} object-contain rounded-lg`}
                                style={{ aspectRatio: 'auto' }}
                                onLoad={() => {
                                    // Ensure the first frame with the loaded image has painted, then try sound
                                    visibleAtRef.current = Date.now();
                                    requestAnimationFrame(() => {
                                        requestAnimationFrame(() => {
                                            maybePlayAppearSound();
                                        });
                                    });
                                }}
                                onError={() => {
                                    // If image fails, still attempt to sync sound soon
                                    requestAnimationFrame(() => {
                                        requestAnimationFrame(() => {
                                            maybePlayAppearSound();
                                        });
                                    });
                                }}
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-3 relative z-20">
                        <button
                            onClick={handleEngagement}
                            disabled={hasEngaged}
                            className={`flex-1 py-2 rounded-lg font-bold ${standalone ? 'text-base' : 'text-sm'} transition-all duration-200 shadow-lg ${hasEngaged
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
                                : 'btn-primary hover:scale-[1.02] active:scale-[0.98]'
                                }`}
                        >
                            {hasEngaged ? (
                                <span className="flex items-center justify-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Accepted
                                </span>
                            ) : (
                                'Accept Challenge'
                            )}
                        </button>

                        <button
                            onClick={handleLater}
                            className={`px-4 py-2 rounded-lg font-medium ${standalone ? 'text-base' : 'text-sm'} btn-secondary transition-all duration-200 broadcast-card-dismiss`}
                        >
                            Later
                        </button>
                        {!standalone && (
                            <button
                                onClick={() => setShowDetails(true)}
                                className={`px-3 py-2 rounded-lg font-medium ${standalone ? 'text-base' : 'text-sm'} bg-white/15 hover:bg-white/25 transition-all`}
                            >
                                Read Challenge
                            </button>
                        )}
                    </div>

                    {/* Subtle glow in non-standalone mode only */}
                    {!standalone && (
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 animate-pulse opacity-20 pointer-events-none" />
                    )}
                    {/* Accepted overlay (only after accept) */}
                    {showAccepted && (
                        <div className="accepted-overlay">
                            <div className="accepted-badge">Challenge Accepted</div>
                        </div>
                    )}
                    {/* Details Modal (dashboard only) */}
                    {!standalone && showDetails && (
                        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
                            <div className="bg-neutral-900 text-white rounded-2xl shadow-2xl w-[min(92vw,42rem)] max-h-[85vh] overflow-auto relative p-5">
                                <button
                                    onClick={() => { setShowDetails(false); handleDismiss(); }}
                                    className="absolute top-3 right-3 text-white/80 hover:text-white"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                <h2 className="text-2xl font-extrabold mb-3">{currentCard.title}</h2>
                                {currentCard.imageUrl && (
                                    <img
                                        src={`${API_BASE_URL}${currentCard.imageUrl}`}
                                        alt={currentCard.title}
                                        className="w-full max-h-[55vh] object-contain rounded-xl mb-4"
                                        style={{ aspectRatio: 'auto' }}
                                    />
                                )}
                                <p className="text-white/90 text-[15px] leading-relaxed mb-4">{currentCard.description}</p>
                                <div className="text-white/70 text-sm">Published: {new Date(currentCard.publishedAt).toLocaleString()}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BroadcastCard;
